import { useCallback } from 'react';

import { expValEquals } from '@confluence/feature-experiments';

import type { TitleAndButtonsProps } from './TitleAndButtons.tsx';
import type { PersistenceMode } from '../../../controller/derivedState.ts';

interface UseRefetchWrapperOnBackClickProps {
	titleAndButtons: TitleAndButtonsProps;
	isSpecificAccessTabOpen: boolean;
	refetchAccessRequest: () => void;
	refreshDialogState: () => Promise<void>;
	persistenceMode?: PersistenceMode;
}

/**
 * Hook that wraps the onBackClick callback to also trigger a refetch when closing
 * the specific access dialog. This ensures fresh data when users reopen the dialog.
 *
 * The wrapper is only applied when ALL conditions are met:
 * 1. Specific access dialog is open
 * 2. Original onBackClick exists
 * 3. persistence_specific_access_gate experiment is enabled
 * 4. NOT in staged mode (publish modal) - staged mode maintains local state
 */
export const useRefetchWrapperOnBackClick = ({
	titleAndButtons,
	isSpecificAccessTabOpen,
	refetchAccessRequest,
	refreshDialogState,
	persistenceMode,
}: UseRefetchWrapperOnBackClickProps): TitleAndButtonsProps => {
	const wrappedOnBackClick = useCallback(async () => {
		refetchAccessRequest();
		titleAndButtons.onBackClick?.();
		await refreshDialogState();
	}, [titleAndButtons, refetchAccessRequest, refreshDialogState]);

	// In staged mode (publish modal), we don't refetch because:
	// 1. Changes are staged locally, not yet committed to the server
	// 2. Refetching would lose the local staged state
	const isLiveMode = persistenceMode !== 'staged';

	const shouldApplyWrapper =
		isSpecificAccessTabOpen &&
		titleAndButtons.onBackClick &&
		expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') &&
		isLiveMode;

	return shouldApplyWrapper
		? {
				...titleAndButtons,
				onBackClick: wrappedOnBackClick,
			}
		: titleAndButtons;
};
